import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const gold = '#D4AF37';
const ivory = '#F7E6BD';
const ivorySoft = 'rgba(247,230,189,0.9)';
const dark = '#0C0C0C';
const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;

const AccordionItem = ({ q, a, index, isOpen, onToggle, prefix }) => (
  <div style={{ borderBottom: '1px solid rgba(212,175,55,0.1)' }}>
    <button
      data-testid={`faq-q-${prefix}-${index}`}
      onClick={onToggle}
      style={{
        width: '100%', background: 'none', border: 'none', cursor: 'pointer',
        padding: '20px 0', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
      }}
    >
      <span style={{
        color: gold, fontSize: 12, flexShrink: 0, transition: 'transform 0.3s',
        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block',
      }}>&#9670;</span>
      <span style={{ fontFamily: "'Georgia', serif", fontSize: 15, color: gold, flex: 1, fontWeight: 600 }}>
        {q}
      </span>
    </button>
    <div style={{ maxHeight: isOpen ? 1200 : 0, overflow: 'hidden', transition: 'max-height 0.35s ease' }}>
      <p style={{
        fontFamily: "'Georgia', serif", fontSize: 14, color: ivorySoft,
        lineHeight: 1.85, margin: '0 0 20px 26px', whiteSpace: 'pre-line',
      }}>
        {a}
      </p>
    </div>
  </div>
);

const SectionTitle = ({ title, subtitle }) => (
  <div style={{ marginBottom: 28 }}>
    <h2 style={{ fontFamily: "'Georgia', serif", fontSize: 22, color: gold, fontWeight: 500, marginBottom: 4, letterSpacing: 0.5 }}>
      {title}
    </h2>
    {subtitle && (
      <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: gold, fontStyle: 'italic', margin: 0, opacity: 0.85 }}>
        {subtitle}
      </p>
    )}
    <div style={{ width: 40, height: 1, backgroundColor: gold, marginTop: 12, opacity: 0.3 }} />
  </div>
);

/* ── Bloc "Notre engagement de confiance" (intro Guide) ── */
const EngagementBlock = ({ intro }) => {
  if (!intro) return null;
  return (
    <div data-testid="engagement-block" style={{
      marginBottom: 40, padding: '28px 26px', borderRadius: 14,
      background: 'linear-gradient(135deg, rgba(212,175,55,0.05), rgba(212,175,55,0.01))',
      border: '1px solid rgba(212,175,55,0.18)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(212,175,55,0.08)',
    }}>
      <h3 style={{
        fontFamily: "'Georgia', serif", fontSize: 19, color: gold, fontWeight: 500,
        margin: '0 0 6px', letterSpacing: 0.5,
      }}>
        {intro.title}
      </h3>
      <p style={{
        fontFamily: "'Georgia', serif", fontSize: 13, color: gold, fontStyle: 'italic',
        margin: '0 0 22px', opacity: 0.85, lineHeight: 1.6,
      }}>
        « {intro.lead} »
      </p>
      <div style={{ display: 'grid', gap: 14 }}>
        {(intro.pillars || []).map((p, i) => (
          <div key={i} data-testid={`engagement-pillar-${i}`} style={{
            display: 'flex', gap: 14, alignItems: 'flex-start',
            paddingTop: i > 0 ? 14 : 0,
            borderTop: i > 0 ? '1px solid rgba(212,175,55,0.08)' : 'none',
          }}>
            <span style={{
              flexShrink: 0, fontFamily: "'Georgia', serif", fontSize: 18, color: gold,
              fontStyle: 'italic', fontWeight: 600, minWidth: 28,
            }}>{i + 1}.</span>
            <div>
              <p style={{
                fontFamily: "'Georgia', serif", fontSize: 14, color: gold,
                fontWeight: 600, margin: '0 0 4px', letterSpacing: 0.3,
              }}>{p.label}</p>
              <p style={{
                fontFamily: "'Georgia', serif", fontSize: 13, color: ivorySoft,
                lineHeight: 1.7, margin: 0,
              }}>{p.body}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Bloc "Question du mois" (footer Guide) ── */
const QuestionDuMois = ({ item }) => {
  if (!item) return null;
  return (
    <div data-testid="question-du-mois" style={{
      marginTop: 48, padding: '24px 26px', borderRadius: 12,
      border: '1px solid rgba(212,175,55,0.3)',
      background: 'rgba(212,175,55,0.04)',
      boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
    }}>
      <p style={{
        fontFamily: "'Georgia', serif", fontSize: 11, color: gold,
        letterSpacing: 3, textTransform: 'uppercase', margin: '0 0 8px', opacity: 0.7,
      }}>
        ✦ Question du mois
      </p>
      <h4 style={{
        fontFamily: "'Georgia', serif", fontSize: 16, color: gold,
        fontWeight: 600, margin: '0 0 10px',
      }}>{item.q}</h4>
      <p style={{
        fontFamily: "'Georgia', serif", fontSize: 14, color: ivorySoft,
        lineHeight: 1.85, margin: 0, whiteSpace: 'pre-line',
      }}>{item.a}</p>
    </div>
  );
};

const tabs = [
  { id: 'transmission', label: "L'Art de la Transmission", short: 'Transmission', icon: '\u2756' },
  { id: 'charte', label: "La Charte du Gardien", short: 'Charte', icon: '\u2736' },
  { id: 'guide', label: "Mode d'Emploi", short: 'Guide', icon: '\u273F' },
];

const FaqPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = ['charte', 'guide'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'transmission';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [openIndex, setOpenIndex] = useState(null);
  const [items, setItems] = useState({ transmission: [], charte: [], guide: [] });
  const [intro, setIntro] = useState(null);
  const [questionDuMois, setQuestionDuMois] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [r1, r2, r3, r4, r5] = await Promise.all([
          fetch(`${API}/univers/transmission`).then(r => r.json()),
          fetch(`${API}/univers/charte`).then(r => r.json()),
          fetch(`${API}/univers/guide`).then(r => r.json()),
          fetch(`${API}/univers/charte/intro`).then(r => r.json()),
          fetch(`${API}/univers/question-du-mois`).then(r => r.json()),
        ]);
        if (cancelled) return;
        setItems({
          transmission: r1?.items || [],
          charte: r2?.items || [],
          guide: r3?.items || [],
        });
        setIntro(r4?.intro || null);
        setQuestionDuMois(r5?.item || null);
      } catch {
        if (!cancelled) setItems({ transmission: [], charte: [], guide: [] });
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const handleToggle = (i) => setOpenIndex(openIndex === i ? null : i);
  const handleTabChange = (id) => {
    setActiveTab(id);
    setOpenIndex(null);
    setSearchParams({ tab: id });
  };

  const currentItems = useMemo(() => items[activeTab] || [], [items, activeTab]);

  const section = useMemo(() => {
    if (activeTab === 'charte') return { title: "La Charte du Gardien", subtitle: "Sécurité & Éthique" };
    if (activeTab === 'guide') return { title: "Mode d'Emploi", subtitle: "Comment utiliser Bobine au quotidien" };
    return { title: "L'Art de la Transmission", subtitle: "L'essence de l'héritage Bobine" };
  }, [activeTab]);

  // Pour les sections themées : grouper par thème (ordre d'apparition)
  const groupedByTheme = useMemo(() => {
    const list = items[activeTab] || [];
    if (!list.some(it => it.theme)) return null;
    const groups = [];
    const map = {};
    list.forEach(it => {
      const t = it.theme || 'Autres';
      if (!map[t]) {
        map[t] = { theme: t, items: [] };
        groups.push(map[t]);
      }
      map[t].items.push(it);
    });
    return groups;
  }, [activeTab, items]);

  return (
    <div data-testid="univers-page" style={{ minHeight: '100vh', backgroundColor: dark, padding: '0 0 80px' }}>
      <div style={{
        borderBottom: '1px solid rgba(212,175,55,0.15)', padding: '16px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span onClick={() => navigate('/')} style={{
          cursor: 'pointer', fontFamily: "'Georgia', serif", fontSize: 20,
          color: gold, letterSpacing: 4, fontWeight: 600,
        }}>BOBINE</span>
        <button data-testid="univers-back-btn" onClick={() => {
          const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('bobine_token');
          navigate(hasToken ? '/dashboard' : '/');
        }} style={{
          fontFamily: "'Georgia', serif", fontSize: 13, color: gold,
          background: 'none', border: '1px solid rgba(212,175,55,0.3)',
          borderRadius: 8, padding: '8px 20px', cursor: 'pointer',
        }}>Retour</button>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 0' }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <p style={{
            fontFamily: "'Georgia', serif", fontSize: 11, color: gold, letterSpacing: 3,
            textTransform: 'uppercase', margin: 0, opacity: 0.6,
          }}>Le Sanctuaire</p>
          <h1 style={{
            fontFamily: "'Georgia', serif", fontSize: 32, color: gold, fontWeight: 500,
            margin: '8px 0 6px', letterSpacing: 1.2,
          }}>L'Univers Bobine</h1>
          <p style={{
            fontFamily: "'Georgia', serif", fontSize: 13, color: ivory, fontStyle: 'italic',
            margin: 0, opacity: 0.75,
          }}>le fil de la mémoire</p>
        </div>

        <div data-testid="univers-tabs" style={{
          display: 'flex', gap: 0, marginBottom: 40, borderBottom: '1px solid rgba(212,175,55,0.15)',
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              data-testid={`univers-tab-${tab.id}`}
              onClick={() => handleTabChange(tab.id)}
              style={{
                flex: 1, fontFamily: "'Georgia', serif", fontSize: 14, padding: '14px 8px',
                background: 'none', border: 'none', cursor: 'pointer',
                color: activeTab === tab.id ? gold : ivory,
                opacity: activeTab === tab.id ? 1 : 0.7,
                borderBottom: activeTab === tab.id ? `2px solid ${gold}` : '2px solid transparent',
                transition: 'all 0.3s', letterSpacing: 0.3,
              }}
            >
              <span style={{ marginRight: 6 }}>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.short}</span>
            </button>
          ))}
        </div>

        <SectionTitle title={section.title} subtitle={section.subtitle} />

        {loading ? (
          <p style={{ fontFamily: "'Georgia', serif", color: ivorySoft, textAlign: 'center', padding: 32, fontStyle: 'italic' }}>
            Chargement...
          </p>
        ) : groupedByTheme ? (
          <>
            {/* Charte : bloc d'engagement (4 piliers) en intro */}
            {activeTab === 'charte' && <EngagementBlock intro={intro} />}
            {groupedByTheme.map((group, gi) => (
              <div key={group.theme} data-testid={`${activeTab}-theme-${gi}`} style={{ marginBottom: 32 }}>
                <h3 style={{
                  fontFamily: "'Georgia', serif", fontSize: 17, color: gold, fontWeight: 500,
                  fontStyle: 'italic', letterSpacing: 0.4, margin: '0 0 12px',
                }}>{group.theme}</h3>
                <div style={{ borderTop: '1px solid rgba(212,175,55,0.15)' }}>
                  {group.items.map((item, i) => {
                    const globalIdx = `${gi}-${i}`;
                    return (
                      <AccordionItem
                        key={item.id || globalIdx}
                        q={item.q}
                        a={item.a}
                        index={i}
                        prefix={`${activeTab}-g${gi}`}
                        isOpen={openIndex === globalIdx}
                        onToggle={() => setOpenIndex(openIndex === globalIdx ? null : globalIdx)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
            {/* Guide : Question du mois en footer */}
            {activeTab === 'guide' && <QuestionDuMois item={questionDuMois} />}
          </>
        ) : currentItems.length === 0 ? (
          <p style={{ fontFamily: "'Georgia', serif", color: ivorySoft, textAlign: 'center', padding: 32, fontStyle: 'italic' }}>
            Cette section est en cours de rédaction.
          </p>
        ) : (
          <div style={{ borderTop: '1px solid rgba(212,175,55,0.15)' }}>
            {currentItems.map((item, i) => (
              <AccordionItem
                key={item.id || `${activeTab}-${i}`}
                q={item.q}
                a={item.a}
                index={i}
                prefix={activeTab}
                isOpen={openIndex === i}
                onToggle={() => handleToggle(i)}
              />
            ))}
          </div>
        )}

        <div style={{ marginTop: 48, textAlign: 'center' }}>
          <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: ivorySoft, lineHeight: 1.8 }}>
            Une question qui n'est pas dans la liste&nbsp;?<br />
            <a href="mailto:bonjour@bobine.live" style={{ color: gold, textDecoration: 'none' }}>bonjour@bobine.live</a>
            <span style={{ color: ivory, opacity: 0.85 }}> — Le Sanctuaire vous répond sous 24h.</span>
          </p>
          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center', gap: 24 }}>
            <a href="/terms" style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: gold, textDecoration: 'none' }}>
              Conditions Générales
            </a>
            <a href="/privacy" style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: gold, textDecoration: 'none' }}>
              Confidentialité
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaqPage;
